/**
 * Slack's Workflow Builder as a large sheet (core WorkflowBuilderViewModel): the workflows I manage, and the editor
 * for one (details, trigger, steps, managers, publish, webhook URL, delete) with its activity log.
 */
import type { Workflow, WorkflowRun, WorkflowStepType } from "@bundle/contracts";
import { WorkflowBuilder, type WorkflowBuilderState, type WorkflowBuilderViewModel, type WorkflowEditor } from "@bundle/core";
import { useMemo, useState } from "react";
import { Banner, Button, Card, Chip, Divider, Icon, IconButton, ListRow, Menu, ScrollView, SegmentedControl, Select, Sheet, Spinner, Stack, Text, TextField, Toast } from "../../primitives/index.ts";
import { useFormat, useT, useTr } from "../../i18n/I18nProvider.tsx";
import { useWorkspace } from "../../hooks/index.ts";
import { StepEditor } from "./StepEditor.tsx";
import { TriggerEditor } from "./TriggerEditor.tsx";
import { useLabel } from "./TemplateField.tsx";

const STATUS_TONE = { published: "success", changed: "warning", draft: "neutral" } as const;

export function WorkflowBuilderSheet({ vm, state }: { vm: WorkflowBuilderViewModel; state: WorkflowBuilderState }) {
  const t = useT();
  const tr = useTr();
  const e = state.editor;
  return (
    <>
      <Sheet
        open={state.open}
        onClose={() => vm.close()}
        size="large"
        title={e === undefined ? t("workflow.builder.title") : e.draft.name.trim() === "" ? t("workflow.editor.untitled") : e.draft.name}
        headerActions={e === undefined ? undefined : <Button label={t("workflow.editor.back")} icon="arrowLeft" variant="ghost" size="sm" onPress={() => vm.back()} testID="workflow-back" />}
        testID="workflow-builder"
      >
        {e === undefined ? <WorkflowList vm={vm} state={state} /> : <Editor vm={vm} state={state} editor={e} />}
      </Sheet>
      <Sheet open={state.confirmLeave !== undefined} onClose={() => vm.confirmLeave(false)} detents={["medium"]} title={t("workflow.leave.title")} testID="workflow-leave">
        <Stack gap="4" padding="4">
          <Text color="text.secondary">{t("workflow.leave.body")}</Text>
          <Stack direction="horizontal" justify="end" gap="2">
            <Button label={t("workflow.leave.stay")} variant="secondary" onPress={() => vm.confirmLeave(false)} testID="workflow-leave-stay" />
            <Button label={t("workflow.leave.discard")} variant="destructive" onPress={() => vm.confirmLeave(true)} testID="workflow-leave-discard" />
          </Stack>
        </Stack>
      </Sheet>
      <Toast open={state.notice !== undefined} onClose={() => vm.dismissNotice()} message={state.notice === undefined ? "" : tr(state.notice)} closeLabel={t("common.close")} durationMs={4000} testID="workflow-notice" />
    </>
  );
}

function WorkflowList({ vm, state }: { vm: WorkflowBuilderViewModel; state: WorkflowBuilderState }) {
  const t = useT();
  const fmt = useFormat();
  return (
    <ScrollView>
      <Stack gap="4" padding="4" testID="workflow-list">
        <Stack direction="horizontal" align="center" gap="3">
          <Stack grow gap="0.5">
            <Text variant="headline" weight="semibold">
              {t("workflow.list.title")}
            </Text>
            <Text variant="footnote" color="text.secondary">
              {t("workflow.list.body")}
            </Text>
          </Stack>
          {state.canCreate ? <Button label={t("workflow.list.new")} icon="plus" variant="primary" onPress={() => vm.create()} testID="workflow-new" /> : null}
        </Stack>
        {state.status === "loading" && state.workflows.length === 0 ? (
          <Stack align="center" padding="6">
            <Spinner accessibilityLabel={t("common.loading")} />
          </Stack>
        ) : state.status === "error" ? (
          <Stack align="center" gap="3" padding="6">
            <Text color="text.secondary">{t("workflow.list.error")}</Text>
            <Button label={t("common.retry")} icon="reload" variant="secondary" onPress={() => void vm.refresh()} testID="workflow-list-retry" />
          </Stack>
        ) : state.workflows.length === 0 ? (
          <Card gap="2">
            <Text weight="semibold">{t("workflow.list.empty.title")}</Text>
            <Text variant="footnote" color="text.secondary">
              {state.canCreate ? t("workflow.list.empty.body") : t("workflow.list.empty.cantCreate")}
            </Text>
          </Card>
        ) : (
          <Stack gap="1">
            {state.workflows.map((w) => (
              <WorkflowRow key={w.id} wf={w} onPress={() => vm.edit(w.id)} subtitle={t("workflow.list.edited", { when: fmt.dateTime(w.updatedAt) })} />
            ))}
          </Stack>
        )}
        {state.canSetCreators ? (
          <Card gap="2" testID="workflow-creators">
            <Text weight="semibold">{t("workflow.creators.title")}</Text>
            <Select
              accessibilityLabel={t("workflow.creators.title")}
              value={state.creators}
              onChange={(c) => void vm.setCreators(c)}
              disabled={state.busy === "creators"}
              options={[
                { value: "everyone", label: t("workflow.creators.everyone") },
                { value: "admins", label: t("workflow.creators.admins") },
              ]}
              testID="workflow-creators-select"
            />
          </Card>
        ) : null}
      </Stack>
    </ScrollView>
  );
}

function WorkflowRow({ wf, onPress, subtitle }: { wf: Workflow; onPress: () => void; subtitle: string }) {
  const t = useT();
  const status = WorkflowBuilder.workflowStatus(wf);
  return (
    <ListRow
      title={wf.definition.name}
      subtitle={wf.definition.description === undefined ? subtitle : `${wf.definition.description} · ${subtitle}`}
      leading={wf.definition.icon === undefined ? <Icon name="sparkles" size="icon.sm" color="text.secondary" /> : <Text>{wf.definition.icon}</Text>}
      trailing={<Chip label={t(`workflow.status.${status}`)} tone={STATUS_TONE[status]} testID={`workflow-row-${wf.id}-status`} />}
      onPress={onPress}
      testID={`workflow-row-${wf.id}`}
    />
  );
}

function Editor({ vm, state, editor: e }: { vm: WorkflowBuilderViewModel; state: WorkflowBuilderState; editor: WorkflowEditor }) {
  const t = useT();
  const wf = e.workflow;
  const status = wf === undefined ? undefined : WorkflowBuilder.workflowStatus(wf);
  return (
    <Stack grow testID="workflow-editor">
      <Stack direction="horizontal" align="center" gap="2" paddingX="4" paddingY="2" wrap>
        {e.id === undefined ? null : (
          <SegmentedControl
            variant="tabs"
            size="sm"
            value={e.tab}
            onChange={(tab) => vm.showTab(tab)}
            options={[
              { value: "build", label: t("workflow.tab.build") },
              { value: "activity", label: t("workflow.tab.activity") },
            ]}
            accessibilityLabel={t("workflow.builder.title")}
            testID="workflow-tabs"
          />
        )}
        <Stack grow direction="horizontal" align="center" gap="2">
          {status === undefined ? null : <Chip label={t(`workflow.status.${status}`)} tone={STATUS_TONE[status]} testID="workflow-status" />}
          {e.dirty ? (
            <Text variant="footnote" color="text.secondary">
              {t("settings.unsaved")}
            </Text>
          ) : null}
        </Stack>
        <Button label={t("workflow.action.test")} icon="play" variant="secondary" size="sm" loading={state.busy === "test"} disabled={state.busy !== undefined || !WorkflowBuilder.canSaveDraft(e.draft)} onPress={() => void vm.testRun()} testID="workflow-test" />
        <Button label={t("workflow.action.save")} variant="secondary" size="sm" loading={state.busy === "save"} disabled={!vm.canSave} onPress={() => void vm.save()} testID="workflow-save" />
        {wf?.published === true && !e.dirty && wf.changed !== true ? (
          <Button label={t("workflow.action.unpublish")} variant="secondary" size="sm" loading={state.busy === "unpublish"} disabled={state.busy !== undefined} onPress={() => void vm.unpublish()} testID="workflow-unpublish" />
        ) : (
          <Button label={wf?.published === true ? t("workflow.action.publishChanges") : t("workflow.action.publish")} variant="primary" size="sm" loading={state.busy === "publish"} disabled={!vm.canPublish} onPress={() => void vm.publish()} testID="workflow-publish" />
        )}
      </Stack>
      <Divider />
      {e.tab === "activity" ? <ActivityLog editor={e} onRefresh={() => void vm.loadRuns()} /> : <BuildTab vm={vm} state={state} editor={e} />}
    </Stack>
  );
}

function BuildTab({ vm, state, editor: e }: { vm: WorkflowBuilderViewModel; state: WorkflowBuilderState; editor: WorkflowEditor }) {
  const t = useT();
  const tr = useTr();
  const d = e.draft;
  const problems = useMemo(() => WorkflowBuilder.splitProblems(e.problems), [e.problems]);
  const missing = useMemo(() => WorkflowBuilder.incomplete(d), [d]);
  const stepItems = WorkflowBuilder.STEP_TYPES.map((type: WorkflowStepType) => ({ id: type, label: tr(WorkflowBuilder.stepTypeLabel(type)), onSelect: () => vm.addStep(type) }));
  return (
    <ScrollView>
      <Stack gap="5" padding="4">
        {e.webhookUrl === undefined ? null : <WebhookUrl url={e.webhookUrl} onDismiss={() => vm.dismissWebhookUrl()} />}
        <Card gap="3" testID="workflow-details">
          <Stack direction="horizontal" gap="2" align="start">
            <Stack width="avatar.lg">
              <TextField label={t("workflow.details.icon")} value={d.icon ?? ""} onChange={(v) => vm.setIcon(v.slice(0, 16))} testID="workflow-icon" />
            </Stack>
            <Stack grow>
              <TextField label={t("workflow.details.name")} value={d.name} onChange={(v) => vm.setName(v.slice(0, 80))} error={missing.name && e.id !== undefined ? t("workflow.problem.noName") : undefined} placeholder={t("workflow.details.name.placeholder")} autoFocus={e.id === undefined} testID="workflow-name" />
            </Stack>
          </Stack>
          <TextField label={t("workflow.details.description")} value={d.description ?? ""} onChange={(v) => vm.setDescription(v.slice(0, 200))} testID="workflow-description" />
        </Card>
        <Stack gap="2">
          <Text variant="headline" weight="semibold">
            {t("workflow.section.trigger")}
          </Text>
          <Card gap="3">
            <TriggerEditor trigger={d.trigger} onType={(type) => vm.setTriggerType(type)} onChange={(trigger) => vm.setTrigger(trigger)} testID="workflow-trigger" />
            {problems.workflow
              .filter((p) => p.code !== "noName" && p.code !== "noSteps")
              .map((p, i) => (
                <Text key={i} variant="caption" color="status.danger" testID={`workflow-problem-${i}`}>
                  {tr(WorkflowBuilder.problemMessage(p))}
                </Text>
              ))}
            {wf(e)?.hasWebhook === true && e.webhookUrl === undefined ? (
              <Stack direction="horizontal" align="center" gap="2">
                <Stack grow><Text variant="footnote" color="text.secondary">
                  {t("workflow.webhook.hidden")}
                </Text></Stack>
                <Button label={t("workflow.webhook.regenerate")} icon="reload" variant="secondary" size="sm" loading={state.busy === "regenerate"} disabled={state.busy !== undefined} onPress={() => void vm.regenerateHook()} testID="workflow-regenerate" />
              </Stack>
            ) : null}
          </Card>
        </Stack>
        <Stack gap="2" testID="workflow-steps">
          <Text variant="headline" weight="semibold">
            {t("workflow.section.steps")}
          </Text>
          {d.steps.length === 0 ? (
            <Text variant="footnote" color="text.secondary">
              {t("workflow.steps.empty")}
            </Text>
          ) : null}
          {d.steps.map((s, i) => (
            <StepEditor
              key={s.id}
              def={d}
              step={s}
              index={i}
              open={e.openStep === s.id}
              problems={problems.steps[s.id] ?? []}
              incomplete={missing.steps.includes(s.id)}
              onToggle={() => vm.toggleStep(s.id)}
              onChange={(step) => vm.updateStep(step)}
              onMove={(by) => vm.moveStep(s.id, by)}
              onRemove={() => vm.removeStep(s.id)}
              appChoices={state.appChoices}
              appIssues={vm.appIssues(s)}
              onChooseAppStep={(appId, callbackId) => vm.chooseAppStep(s.id, appId, callbackId)}
              testID="workflow-step"
            />
          ))}
          <Stack direction="horizontal">
            <Menu items={stepItems} testID="workflow-add-step-menu">
              <Button label={t("workflow.steps.add")} icon="plus" variant="secondary" onPress={() => undefined} disabled={d.steps.length >= 30} testID="workflow-add-step" />
            </Menu>
          </Stack>
        </Stack>
        <Managers managers={e.managers} createdBy={e.workflow?.createdBy} onChange={(m) => vm.setManagers(m)} />
        {e.id === undefined ? null : <DeleteWorkflow busy={state.busy === "delete"} onDelete={() => void vm.remove()} />}
      </Stack>
    </ScrollView>
  );
}

const wf = (e: WorkflowEditor) => e.workflow;

function WebhookUrl({ url, onDismiss }: { url: string; onDismiss: () => void }) {
  const t = useT();
  const [copied, setCopied] = useState(false);
  return (
    <Card gap="2" testID="workflow-webhook-url">
      <Banner tone="warning" message={t("workflow.webhook.once")} testID="workflow-webhook-once" />
      <Text mono selectable testID="workflow-webhook-url-text">
        {url}
      </Text>
      <Stack direction="horizontal" gap="2" justify="end">
        <Button
          label={copied ? t("integrations.webhook.copied") : t("integrations.webhook.copy")}
          icon="copy"
          variant="secondary"
          onPress={() =>
            void navigator.clipboard?.writeText(url).then(
              () => setCopied(true),
              () => undefined,
            )
          }
          testID="workflow-webhook-copy"
        />
        <Button label={t("workflow.webhook.done")} variant="ghost" onPress={onDismiss} testID="workflow-webhook-done" />
      </Stack>
    </Card>
  );
}

function Managers({ managers, createdBy, onChange }: { managers: string[]; createdBy: string | undefined; onChange: (m: string[]) => void }) {
  const t = useT();
  const ws = useWorkspace();
  const cache = ws.state.cache;
  const people = useMemo(() => (cache === undefined ? [] : WorkflowBuilder.workflowPeople(cache, true)), [cache]);
  const name = (id: string) => cache?.members[id]?.displayName ?? id;
  const owner = createdBy ?? ws.state.meId;
  const options = people.filter((p) => p.id !== owner && !managers.includes(p.id)).map((p) => ({ value: p.id, label: p.name }));
  return (
    <Stack gap="2" testID="workflow-managers">
      <Text variant="headline" weight="semibold">
        {t("workflow.managers.title")}
      </Text>
      <Card gap="3">
        <Text variant="footnote" color="text.secondary">
          {t("workflow.managers.body")}
        </Text>
        <Stack direction="horizontal" gap="1" wrap>
          <Chip label={t("workflow.managers.creator", { name: name(owner) })} tone="accent" testID="workflow-manager-creator" />
          {managers.map((m) => (
            <Chip key={m} label={name(m)} onRemove={() => onChange(managers.filter((x) => x !== m))} removeLabel={t("workflow.managers.remove", { name: name(m) })} testID={`workflow-manager-${m}`} />
          ))}
        </Stack>
        {options.length === 0 || managers.length >= 20 ? null : (
          <Select accessibilityLabel={t("workflow.managers.add")} placeholder={t("workflow.managers.add")} value={undefined} onChange={(id) => onChange([...managers, id])} options={options} testID="workflow-managers-add" />
        )}
      </Card>
    </Stack>
  );
}

function DeleteWorkflow({ busy, onDelete }: { busy: boolean; onDelete: () => void }) {
  const t = useT();
  const [confirm, setConfirm] = useState(false);
  return (
    <Stack direction="horizontal" align="center" gap="2" testID="workflow-delete">
      {confirm ? (
        <>
          <Stack grow><Text variant="footnote" color="status.danger">
            {t("workflow.delete.confirm")}
          </Text></Stack>
          <Button label={t("common.cancel")} variant="ghost" size="sm" onPress={() => setConfirm(false)} testID="workflow-delete-cancel" />
          <Button label={t("workflow.delete.action")} variant="destructive" size="sm" loading={busy} onPress={onDelete} testID="workflow-delete-confirm" />
        </>
      ) : (
        <Button label={t("workflow.delete.action")} icon="trash" variant="ghost" size="sm" onPress={() => setConfirm(true)} testID="workflow-delete-start" />
      )}
    </Stack>
  );
}

const RUN_TONE = { running: "info", waiting: "info", completed: "success", failed: "danger", cancelled: "neutral" } as const;
const STEP_TONE = { running: "info", waiting: "info", done: "success", skipped: "neutral", failed: "danger" } as const;

function ActivityLog({ editor: e, onRefresh }: { editor: WorkflowEditor; onRefresh: () => void }) {
  const t = useT();
  return (
    <ScrollView>
      <Stack gap="3" padding="4" testID="workflow-activity">
        <Stack direction="horizontal" align="center">
          <Stack grow><Text variant="headline" weight="semibold">
            {t("workflow.activity.title")}
          </Text></Stack>
          <IconButton icon="reload" accessibilityLabel={t("workflow.activity.refresh")} onPress={onRefresh} testID="workflow-activity-refresh" />
        </Stack>
        {e.runsStatus === "loading" ? (
          <Stack align="center" padding="6">
            <Spinner accessibilityLabel={t("common.loading")} />
          </Stack>
        ) : e.runsStatus === "error" ? (
          <Text color="text.secondary">{t("workflow.activity.error")}</Text>
        ) : (e.runs ?? []).length === 0 ? (
          <Text color="text.secondary" testID="workflow-activity-empty">
            {t("workflow.activity.empty")}
          </Text>
        ) : (
          (e.runs ?? []).map((r) => <RunCard key={r.id} run={r} editor={e} />)
        )}
      </Stack>
    </ScrollView>
  );
}

function RunCard({ run, editor: e }: { run: WorkflowRun; editor: WorkflowEditor }) {
  const t = useT();
  const fmt = useFormat();
  const label = useLabel();
  const ws = useWorkspace();
  const who = run.userId === undefined ? undefined : (ws.state.cache?.members[run.userId]?.displayName ?? run.userId);
  return (
    <Card gap="2" testID={`workflow-run-${run.id}`}>
      <Stack direction="horizontal" align="center" gap="2" wrap>
        <Chip label={t(`workflow.run.status.${run.status}`)} tone={RUN_TONE[run.status]} testID={`workflow-run-${run.id}-status`} />
        {run.test === true ? <Chip label={t("workflow.run.test")} testID={`workflow-run-${run.id}-test`} /> : null}
        <Stack grow><Text variant="footnote" color="text.secondary">
          {who === undefined ? fmt.dateTime(run.startedAt) : t("workflow.run.by", { name: who, when: fmt.dateTime(run.startedAt) })}
        </Text></Stack>
        <Text variant="caption" color="text.tertiary">
          {t("workflow.run.version", { version: run.version })}
        </Text>
      </Stack>
      {run.error === undefined ? null : (
        <Text variant="footnote" color="status.danger">
          {run.error}
        </Text>
      )}
      {run.steps.map((s) => {
        const index = e.draft.steps.findIndex((x) => x.id === s.stepId);
        const step = e.draft.steps[index];
        const title = step === undefined ? s.stepId : `${index + 1}. ${label(WorkflowBuilder.stepTitle(step))}`;
        const outputs = Object.entries(s.outputs ?? {});
        return (
          <Stack key={s.stepId} gap="0.5" paddingX="2" testID={`workflow-run-${run.id}-step-${s.stepId}`}>
            <Stack direction="horizontal" align="center" gap="2">
              <Chip label={t(`workflow.run.step.${s.status}`)} tone={STEP_TONE[s.status]} />
              <Stack grow><Text variant="footnote" lines={1}>
                {title}
              </Text></Stack>
              {s.attempts > 1 ? (
                <Text variant="caption" color="text.tertiary">
                  {t("workflow.run.attempts", { count: s.attempts })}
                </Text>
              ) : null}
            </Stack>
            {s.error === undefined ? null : (
              <Text variant="caption" color="status.danger">
                {s.error}
              </Text>
            )}
            {outputs.length === 0 ? null : (
              <Text variant="caption" color="text.tertiary" selectable>
                {outputs.map(([k, v]) => `${k}: ${v.value}`).join(" · ")}
              </Text>
            )}
          </Stack>
        );
      })}
    </Card>
  );
}
